<!-- 환산계수 상세 -->
<template>
    <vvo v-slot="v">
        <div class="detail-container">
            <!-- 1. 데이터 없음 표시 영역 -->
            <div class="detail-placeholder" v-if="!selectedRow && !isNewMode">
                <span class="detail-notification-icon">
                    <i class="fas fa-external-link-alt"></i>
                </span>
                <span class="detail-notification-text">환산계수를 선택하세요.</span>
            </div>
            <!-- 2.상세 정보 영역 -->
            <div v-if="selectedRow || isNewMode" class="detail-content-container">
                <!-- 2.1 상세 정보 헤더 영역 -->
                <div class="detail-header" v-if="selectedRow || isNewMode">
                    <div class="detail-title">
                        <span class="title">{{ viewTitle }}</span>
                        <span class="badge title1" v-b-tooltip.hover title="환산계수 식별번호">
                            {{ selectedRow?.conversionIdx || "신규" }}
                        </span>
                        <span class="badge title2">
                            {{ selectedRow?.fromUnit || "신규" }} → {{ selectedRow?.toUnit || "-" }}
                        </span>
                    </div>
                    <div class="detail-control" v-if="!isNewMode">
                        <b-form-checkbox switch v-model="isEditMode" size="lg" />
                    </div>
                </div>

                <!-- 2.2 상세 정보 컨텐츠 표시 영역 -->
                <div class="detail-content" v-if="selectedRow || isNewMode">
                    <div class="form-container">
                        <div class="form-fields-wrapper">
                            <!-- 기본 정보 그룹: 환산계수 핵심 정보 -->
                            <!-- 원본 정보 (타입 + 단위) -->
                            <div class="form-row">
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">원본 타입</label>
                                        <select
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.fromType"
                                            class="form-control"
                                            :disabled="!isNewMode"
                                        >
                                            <option value="">선택 안함</option>
                                            <option
                                                v-for="option in energySourceOptions"
                                                :key="option.value"
                                                :value="option.value"
                                            >
                                                {{ option.text }}
                                            </option>
                                        </select>
                                        <select v-else class="form-control" disabled>
                                            <option value="">
                                                {{ getEnergySourceLabel(detailInfo?.fromType) || "-" }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">원본 단위</label>
                                        <select
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.fromUnit"
                                            class="form-control"
                                            :disabled="!isNewMode"
                                        >
                                            <option value="">선택 안함</option>
                                            <option
                                                v-for="option in fromUnitOptions"
                                                :key="option.value"
                                                :value="option.value"
                                            >
                                                {{ option.text }}
                                            </option>
                                        </select>
                                        <select v-else class="form-control" disabled>
                                            <option value="">{{ detailInfo?.fromUnit || "-" }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <!-- 목표 정보 (타입 + 단위) -->
                            <div class="form-row">
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">목표 타입</label>
                                        <select
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.toType"
                                            class="form-control"
                                            :disabled="!isNewMode"
                                        >
                                            <option value="">선택 안함</option>
                                            <option
                                                v-for="option in energySourceOptions"
                                                :key="option.value"
                                                :value="option.value"
                                            >
                                                {{ option.text }}
                                            </option>
                                        </select>
                                        <select v-else class="form-control" disabled>
                                            <option value="">
                                                {{ getEnergySourceLabel(detailInfo?.toType) || "-" }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">목표 단위</label>
                                        <select
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.toUnit"
                                            class="form-control"
                                            :disabled="!isNewMode"
                                        >
                                            <option value="">선택 안함</option>
                                            <option
                                                v-for="option in toUnitOptions"
                                                :key="option.value"
                                                :value="option.value"
                                            >
                                                {{ option.text }}
                                            </option>
                                        </select>
                                        <select v-else class="form-control" disabled>
                                            <option value="">{{ detailInfo?.toUnit || "-" }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <!-- 환산 계수 + 사용 여부 -->
                            <div class="form-row">
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">환산 계수</label>
                                        <input
                                            v-if="selectedRow || isNewMode"
                                            v-model.number="detailInfo.conversionFactor"
                                            type="number"
                                            step="any"
                                            class="form-control"
                                            :disabled="!isEditMode"
                                            placeholder="예: 0.00022"
                                        />
                                        <input v-else type="text" class="form-control" disabled />
                                    </div>
                                </div>
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">사용 여부</label>
                                        <select
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.enabled"
                                            class="form-control"
                                            :disabled="!isEditMode"
                                        >
                                            <option value="Y">사용</option>
                                            <option value="N">미사용</option>
                                        </select>
                                        <select v-else class="form-control" disabled>
                                            <option>{{ detailInfo?.enabled === "Y" ? "사용" : "미사용" }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <!-- 적용 기간 그룹 -->
                            <div class="form-row">
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">적용 시작일</label>
                                        <DatePicker
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.effectiveFrom"
                                            :disabled="isEffectiveFromDisabled"
                                            :disabled-date="disabledEffectiveFromDate"
                                            value-type="format"
                                            format="YYYY-MM-DD"
                                            :editable="false"
                                            :clearable="true"
                                            input-class="form-control"
                                            placeholder="YYYY-MM-DD"
                                        />
                                        <input v-else type="text" class="form-control" value="" disabled />
                                        <small
                                            class="form-text text-muted"
                                            v-if="isNewMode || (detailInfo && !detailInfo.effectiveFrom)"
                                        >
                                            <span v-if="isNewMode">
                                                * 시작일을 선택할 수 있습니다. 비워두면 전체 기간 적용됩니다 (무기한
                                                과거부터).
                                            </span>
                                            <span v-else-if="detailInfo && !detailInfo.effectiveFrom">
                                                * 무기한 과거부터 적용되는 환산계수입니다. 시작일은 변경할 수 없습니다.
                                            </span>
                                        </small>
                                    </div>
                                </div>
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">적용 종료일</label>
                                        <DatePicker
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.effectiveTo"
                                            :disabled="isEffectiveToDisabled"
                                            :disabled-date="disabledEffectiveToDate"
                                            value-type="format"
                                            format="YYYY-MM-DD"
                                            :editable="false"
                                            :clearable="true"
                                            input-class="form-control"
                                            placeholder="YYYY-MM-DD"
                                        />
                                        <input v-else type="text" class="form-control" value="현재 적용중" disabled />
                                        <small
                                            class="form-text text-muted"
                                            v-if="isNewMode || (detailInfo && !detailInfo.effectiveTo)"
                                        >
                                            <span v-if="isNewMode">
                                                * 종료일은 선택할 수 없습니다. 항상 NULL로 저장됩니다 (현재 적용중).
                                            </span>
                                            <span v-else-if="detailInfo && !detailInfo.effectiveTo">
                                                * 무기한까지 적용되는 환산계수입니다. 종료일은 변경할 수 없습니다.
                                            </span>
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <!-- 참조 정보 그룹 -->
                            <div class="form-row">
                                <div class="form-col">
                                    <div class="form-group">
                                        <label class="form-label">참조 출처</label>
                                        <input
                                            v-if="selectedRow || isNewMode"
                                            v-model="detailInfo.referenceSource"
                                            type="text"
                                            class="form-control"
                                            :disabled="!isEditMode"
                                            placeholder="예: 에너지법 시행규칙 [별표4]"
                                        />
                                        <input v-else type="text" class="form-control" disabled />
                                    </div>
                                </div>
                                <div class="form-col"></div>
                            </div>

                            <!-- 비고 (전체 너비) -->
                            <div class="form-group form-group-full">
                                <label class="form-label">비고</label>
                                <b-form-textarea
                                    v-if="selectedRow || isNewMode"
                                    v-model="detailInfo.remarks"
                                    :disabled="!isEditMode"
                                    placeholder="추가 설명 입력"
                                    rows="3"
                                />
                                <textarea v-else class="form-control" disabled rows="3"></textarea>
                            </div>
                        </div>

                        <!-- 등록 정보 -->
                        <div class="info-group" v-if="!isNewMode && selectedRow && detailInfo.conversionIdx">
                            <div class="info-row-single">
                                <span class="info-text">
                                    등록자: {{ detailInfo.regWorker || "-" }} ({{ detailInfo.regDate || "-" }})
                                </span>
                                <span class="info-separator"></span>
                                <span class="info-text">
                                    수정자: {{ detailInfo.modWorker || "-" }} ({{ detailInfo.modDate || "-" }})
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 3.하단 영역 -->
            <div class="detail-bottom">
                <!-- 3.2 하단 제어 버튼 -->
                <div class="detail-bottom-buttons">
                    <button
                        type="button"
                        class="btn btn-primary detail-btn"
                        v-if="isEditMode"
                        @click="onValidate(v, saveData)"
                    >
                        저장
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary detail-btn"
                        v-if="selectedRow || isNewMode"
                        @click="handleClose($event)"
                    >
                        닫기
                    </button>
                </div>
            </div>
        </div>
    </vvo>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import * as popupMessages from "@src/consts/popupMessageConsts";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import xelib from "xelib";

const { Unit } = xelib.PointBoxV2;

export default {
    components: {
        DatePicker,
    },
    props: ["title", "selectedRow"],
    async created() {},
    mounted() {},
    data() {
        return {
            detailInfo: {
                conversionIdx: null,
                fromUnit: "", // 빈 문자열로 초기화하여 "선택 안함" 옵션과 매칭
                toUnit: "", // 빈 문자열로 초기화하여 "선택 안함" 옵션과 매칭
                fromType: "", // 빈 문자열로 초기화하여 "선택 안함" 옵션과 매칭
                toType: "", // 빈 문자열로 초기화하여 "선택 안함" 옵션과 매칭
                conversionFactor: null,
                effectiveFrom: null,
                effectiveTo: null,
                referenceSource: null,
                remarks: null,
                enabled: "Y", // 기본값: 사용
                regWorker: null,
                regDate: null,
                modWorker: null,
                modDate: null,
            },
            mode: "view", // 편집모드 : edit/view/new
            popupMessages,
        };
    },
    computed: {
        isEditMode: {
            get() {
                return this.mode == "edit" || this.mode == "new";
            },
            set(value) {
                this.mode = value ? "edit" : "view";
            },
        },
        isNewMode() {
            return this.mode === "new";
        },
        isViewMode() {
            return this.mode === "view";
        },
        viewTitle() {
            return this.title;
        },
        // 모든 단위를 옵션으로 제공 (타입 필터링 없음)
        fromUnitOptions() {
            if (!Unit || !Unit.types) {
                return [];
            }

            // 모든 타입의 단위를 수집하여 중복 제거 후 반환
            const allUnits = new Set();
            Object.keys(Unit.types).forEach((typeKey) => {
                const units = Object.keys(Unit.types[typeKey]);
                units.forEach((unit) => allUnits.add(unit));
            });

            return Array.from(allUnits)
                .sort()
                .map((unit) => ({
                    text: unit,
                    value: unit,
                }));
        },
        // 모든 단위를 옵션으로 제공 (타입 필터링 없음)
        toUnitOptions() {
            if (!Unit || !Unit.types) {
                return [];
            }

            // 모든 타입의 단위를 수집하여 중복 제거 후 반환
            const allUnits = new Set();
            Object.keys(Unit.types).forEach((typeKey) => {
                const units = Object.keys(Unit.types[typeKey]);
                units.forEach((unit) => allUnits.add(unit));
            });

            return Array.from(allUnits)
                .sort()
                .map((unit) => ({
                    text: unit,
                    value: unit,
                }));
        },
        // Unit.types의 키(타입)를 기반으로 에너지 소스 옵션 생성
        energySourceOptions() {
            if (!Unit || !Unit.types) {
                return [{ text: "-", value: "-" }];
            }

            const types = Object.keys(Unit.types).map((type) => {
                // 타입 이름 그대로 표시 (소문자)
                return {
                    text: type,
                    value: type, // 소문자로 저장 (백엔드 요구사항)
                };
            });

            return types.sort((a, b) => a.text.localeCompare(b.text));
        },
        // 적용 시작일 데이트피커 비활성화 여부
        isEffectiveFromDisabled() {
            if (!this.isEditMode) return true;

            // 신규 모드: 항상 선택 가능
            if (this.isNewMode) return false;

            // 수정 모드: effectiveFrom이 NULL이면 변경 불가
            if (this.detailInfo && !this.detailInfo.effectiveFrom) {
                return true; // 무기한 과거 시작점은 보호됨
            }

            return false;
        },
        // 적용 종료일 데이트피커 비활성화 여부
        isEffectiveToDisabled() {
            if (!this.isEditMode) return true;

            // 신규 모드: 항상 비활성화 (effectiveTo는 항상 NULL)
            if (this.isNewMode) return true;

            // 수정 모드: effectiveTo가 NULL이면 변경 불가 (현재 활성 환산계수)
            if (this.detailInfo && !this.detailInfo.effectiveTo) {
                return true; // 현재 활성 환산계수는 새 환산계수 추가로만 마감
            }

            return false;
        },
    },
    watch: {
        selectedRow: {
            handler(newVal, oldVal) {
                // selectedRow가 null이 되면 데이터 초기화
                if (!newVal) {
                    if (!this.isNewMode) {
                        this.clearData();
                    }
                    return;
                }

                // conversionIdx가 없으면 처리하지 않음
                if (!newVal.conversionIdx) {
                    return;
                }

                // 같은 conversionIdx이고 이미 데이터가 로드된 상태면 중복 호출 방지
                // 단, 다른 행에서 같은 행으로 돌아온 경우는 다시 로드
                if (oldVal && oldVal.conversionIdx === newVal.conversionIdx) {
                    // 객체 참조가 다르면 (새로 선택된 경우) 다시 로드
                    if (oldVal !== newVal) {
                        this.loadData();
                    }
                    return;
                }

                // 새로운 항목이거나 다른 항목이면 API를 통해 최신 데이터를 가져옴
                this.loadData();
            },
            immediate: false,
            deep: true,
        },
    },
    methods: {
        // 에너지 소스 코드를 텍스트로 변환
        getEnergySourceLabel(value) {
            if (!value) return "";

            // Unit.types에서 타입 찾기
            if (Unit && Unit.types && Unit.types[value]) {
                // 타입 이름 그대로 반환 (소문자)
                return value;
            }

            return value;
        },
        // list-Detail Common Functions
        async notifyCancelEdit() {
            // return true : Edit Cancel & Proceed Next Step
            //        false: Stop.
            if (this.isEditMode) {
                const result = await this.alertConfirmWarning("편집중인 내용을 무시하고 종료할까요?");
                if (result.value) return true;
                else return false;
            }
            return true;
        },

        // list-Detail Data Functions
        newData() {
            this.mode = "new";
            // 모든 필드를 초기화 (타입과 단위는 빈 문자열로 초기화하여 "선택 안함" 옵션과 매칭)
            Object.keys(this.detailInfo).forEach((key) => {
                if (key === "fromType" || key === "toType" || key === "fromUnit" || key === "toUnit") {
                    this.detailInfo[key] = "";
                } else if (key === "enabled") {
                    // enabled는 기본값 "Y" (사용)
                    this.detailInfo[key] = "Y";
                } else {
                    this.detailInfo[key] = null;
                }
            });
            return true;
        },

        /**
         * 단위 값이 해당 타입의 유효한 단위인지 검증
         * @param {string|null|undefined} unit - 검증할 단위 값
         * @param {string|null|undefined} type - 단위가 속한 타입
         * @returns {boolean} 유효한 단위이면 true, 아니면 false
         */
        isValidUnit(unit, type) {
            // 단위 값이 없으면 유효하지 않음
            if (!unit || unit === "" || unit === null || unit === undefined) {
                return false;
            }

            // 타입 값이 없으면 유효하지 않음
            if (!type || type === "" || type === null || type === undefined) {
                return false;
            }

            // Unit.types가 없으면 유효하지 않음
            if (!Unit?.types) {
                return false;
            }

            // 대소문자 구분 없이 타입 키 찾기 (예: tCO2, primaryEnergy)
            const typeKey = Object.keys(Unit.types).find((key) => key.toLowerCase() === type.toLowerCase());

            // 타입 키를 찾지 못했거나 해당 타입의 단위 목록이 없으면 유효하지 않음
            if (!typeKey || !Unit.types[typeKey]) {
                return false;
            }

            // 해당 타입의 단위 목록에 포함되어 있는지 확인
            const validUnits = Object.keys(Unit.types[typeKey]);
            return validUnits.includes(unit);
        },

        /**
         * API에서 데이터를 로드하여 detailInfo에 반영
         * 타입 → 단위 → 기타 필드 순서로 설정하여 의존성 문제 방지
         */
        async loadData() {
            // conversionIdx 검증
            if (!this.selectedRow?.conversionIdx) {
                console.warn("conversionIdx is not defined");
                return;
            }

            try {
                // API 호출
                const result = await backEndApi.unitConversion.getConversion(this.selectedRow.conversionIdx);
                if (result.status !== 200) {
                    this.alertWarning(`${result.data.message}`);
                    return;
                }

                // 데이터 복사 (참조 문제 방지)
                const apiData = JSON.parse(JSON.stringify(result.data));

                // ===== 1단계: 타입 필드 설정 =====
                // 타입은 null이면 빈 문자열로, 값이 있으면 그대로 사용
                this.detailInfo.fromType = apiData.fromType == null ? "" : apiData.fromType;
                this.detailInfo.toType = apiData.toType == null ? "" : apiData.toType;

                // ===== 2단계: 단위 필드 설정 =====
                // API 데이터에서 단위 값을 그대로 사용 (검증 없음)
                this.detailInfo.fromUnit = apiData.fromUnit == null ? "" : apiData.fromUnit;
                this.detailInfo.toUnit = apiData.toUnit == null ? "" : apiData.toUnit;

                // ===== 3단계: 나머지 필드 설정 =====
                // 타입과 단위를 제외한 나머지 필드 설정
                const excludeKeys = ["fromType", "toType", "fromUnit", "toUnit"];
                Object.keys(this.detailInfo).forEach((key) => {
                    // 이미 설정한 필드는 스킵
                    if (excludeKeys.includes(key)) {
                        return;
                    }

                    // API 데이터에 값이 있으면 사용
                    const value = apiData[key];
                    if (value !== undefined) {
                        // enabled는 "Y"/"N" 문자열 그대로 사용 (boolean이면 변환)
                        if (key === "enabled") {
                            this.detailInfo[key] = value === true || value === "true" || value === "Y" ? "Y" : "N";
                        } else {
                            this.detailInfo[key] = value;
                        }
                    } else {
                        // enabled는 기본값 "Y" (사용), 나머지는 null
                        if (key === "enabled") {
                            this.detailInfo[key] = "Y";
                        } else {
                            this.detailInfo[key] = null;
                        }
                    }
                });

                // 뷰 모드로 전환
                this.mode = "view";
            } catch (e) {
                console.error("Error loading data:", e);
                this.alertNoti(popupMessages.COMMON_NO_SEARCH_DATA_POPUP_MESSAGE);
            }
        },

        clearData() {
            // 모든 필드를 초기화 (타입과 단위는 빈 문자열로 초기화하여 "선택 안함" 옵션과 매칭)
            Object.keys(this.detailInfo).forEach((key) => {
                if (key === "fromType" || key === "toType" || key === "fromUnit" || key === "toUnit") {
                    this.detailInfo[key] = "";
                } else if (key === "enabled") {
                    // enabled는 기본값 "Y" (사용)
                    this.detailInfo[key] = "Y";
                } else {
                    this.detailInfo[key] = null;
                }
            });
            this.mode = "view";
        },
        // 적용 시작일에서 선택 불가능한 날짜 비활성화
        disabledEffectiveFromDate(date) {
            // disabled prop이 true면 모든 날짜 비활성화
            if (this.isEffectiveFromDisabled) {
                return true;
            }

            // 신규 모드: 모든 날짜 선택 가능
            if (this.isNewMode) {
                return false;
            }

            // 수정 모드: effectiveTo가 있으면 effectiveTo 이후 날짜는 선택 불가
            if (this.detailInfo && this.detailInfo.effectiveTo) {
                const effectiveToDate = new Date(this.detailInfo.effectiveTo + "T00:00:00");
                effectiveToDate.setHours(23, 59, 59, 999); // 하루 끝까지
                // effectiveTo 이후 날짜는 비활성화
                return date > effectiveToDate;
            }

            // effectiveTo가 없으면 모든 날짜 선택 가능
            return false;
        },
        // 적용 종료일에서 선택 불가능한 날짜 비활성화
        disabledEffectiveToDate(date) {
            // disabled prop이 true면 모든 날짜 비활성화
            if (this.isEffectiveToDisabled) {
                return true;
            }

            // 신규 모드: 모든 날짜 비활성화 (이미 disabled prop으로 처리되지만 이중 안전장치)
            if (this.isNewMode) {
                return true;
            }

            // 수정 모드: effectiveFrom이 있으면 effectiveFrom 이전 날짜는 선택 불가
            if (this.detailInfo && this.detailInfo.effectiveFrom) {
                const effectiveFromDate = new Date(this.detailInfo.effectiveFrom + "T00:00:00");
                // effectiveFrom 이전 날짜는 비활성화
                return date < effectiveFromDate;
            }

            // effectiveFrom이 없으면 모든 날짜 선택 가능 (하지만 이 경우는 거의 없을 것)
            return false;
        },
        formatEnergySourceCode(payload) {
            // 저장 시에는 소문자로 변환 (백엔드 요구사항)
            if (payload.fromType && typeof payload.fromType === "string") {
                payload.fromType = payload.fromType.toLowerCase();
            }
            if (payload.toType && typeof payload.toType === "string") {
                payload.toType = payload.toType.toLowerCase();
            }
        },
        async saveData() {
            const confirm = await this.alertConfirmWarning(popupMessages.COMMON_SAVE_CONFIRM_POPUP_MESSAGE);
            if (!confirm.value) return;
            try {
                const payload = { ...this.detailInfo };

                // 빈 문자열을 null로 변환
                if (payload.fromType === "") payload.fromType = null;
                if (payload.toType === "") payload.toType = null;
                if (payload.fromUnit === "") payload.fromUnit = null;
                if (payload.toUnit === "") payload.toUnit = null;
                if (payload.effectiveFrom === "") payload.effectiveFrom = null;
                if (payload.effectiveTo === "") payload.effectiveTo = null;
                if (payload.referenceSource === "") payload.referenceSource = null;
                if (payload.remarks === "") payload.remarks = null;

                // enabled는 이미 "Y"/"N" 문자열이므로 그대로 사용
                // 값이 없으면 기본값 "Y"로 설정
                if (payload.enabled === undefined || payload.enabled === null || payload.enabled === "") {
                    payload.enabled = "Y";
                }

                const isInsert = this.isNewMode || !payload.conversionIdx;

                // 수정 모드일 경우 fromUnit, toUnit, fromType, toType만 제외 (conversionFactor는 수정 가능)
                if (!isInsert) {
                    delete payload.fromUnit;
                    delete payload.toUnit;
                    delete payload.fromType;
                    delete payload.toType;

                    // 신규 모드가 아니면 effectiveTo는 항상 null (백엔드 규칙에 따라)
                    // 수정 모드에서 effectiveTo가 null인 경우(현재 활성)는 변경 불가하므로 제외
                    // 이력 환산계수의 경우에만 effectiveTo를 업데이트
                    if (!payload.effectiveTo) {
                        // 현재 활성 환산계수(effectiveTo=null)인 경우 effectiveTo는 업데이트하지 않음
                        delete payload.effectiveTo;
                    }
                } else {
                    // 신규 모드: effectiveTo는 항상 null
                    payload.effectiveTo = null;
                }
                this.formatEnergySourceCode(payload);
                const result = isInsert
                    ? await backEndApi.unitConversion.addConversion(payload)
                    : await backEndApi.unitConversion.updateConversion(payload);

                if (result.status == 200) {
                    await this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE);

                    if (isInsert) this.$emit("data-inserted", result.data);
                    else this.$emit("data-updated", result.data);

                    // 닫지 않고 목록을 새로 불러와서 유지
                    this.mode = "view";
                    if (!isInsert) {
                        await this.loadData();
                    }
                } else {
                    this.alertWarning(
                        `${result.data.message}`,
                        "",
                        `${typeof result.data.detail == "string" ? result.data.detail : ""}`
                    );
                }
            } catch (e) {
                console.error(e);
                this.alertDanger("저장 중 오류가 발생했습니다.", "", e.message || "");
            }
        },

        getData() {
            return this.detailInfo;
        },

        // Private Event Handling Functions
        async handleClose(event) {
            const result = await this.notifyCancelEdit();
            if (!result) {
                event && event.preventDefault();
                return false;
            }
            this.clearData();
            this.$emit("closed");
            return true;
        },
    },
};
</script>

<style scoped>
.detail-control {
    margin-bottom: 8px;
}
.detail-content-container {
    height: calc(100% - 70px);
}

/* detail area styles */
.detail-placeholder {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.detail-notification-icon {
    padding: 1rem;
    color: #444;
    font-size: 48px;
}

.detail-notification-text {
    font-size: 15px;
    font-weight: bold;
}

.detail-container {
    height: 100%;
    width: 100%;
    padding: 1rem 1rem 1rem 1rem;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    font-size: 12px;
}

.detail-header {
    margin-bottom: 0px !important;
    padding: 8px 16px 8px 16px;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px 6px 0px 0px;
    background-color: #f1f1f1;
    border: solid #ececec 1px;
    border-bottom: 1px solid #ccc;
    font-weight: bold;
}

.detail-header .detail-title {
    padding: 0 !important;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
}

.detail-content {
    height: calc(100% - 50px);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    margin-top: 2px;
    padding: 20px;
    overflow-y: auto;
    background-color: white;
    border: solid #ececec 1px;
    border-radius: 0px 0px 6px 6px;
}

.form-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.form-fields-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    /* margin-bottom: 1rem; */
}

.form-col {
    display: flex;
    flex-direction: column;
}

.form-group {
    margin-bottom: 0;
}

.form-row .form-group {
    margin-bottom: 1rem;
}

.form-group-full {
    /* margin-top: 0.5rem; */
    max-width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    /* min-height: 0; */
}

.form-group-full .form-label {
    flex-shrink: 0;
}

.form-group-full textarea,
.form-group-full ::v-deep textarea {
    min-height: 70%;
    resize: none;
}

@media (max-width: 768px) {
    .form-row {
        grid-template-columns: 1fr;
        gap: 0;
    }
}

.form-label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    font-size: 13px;
}

.form-text {
    font-size: 11px;
    margin-top: 0.25rem;
}

.info-group {
    margin-top: auto;
    padding-top: 1rem;
    border-top: 2px solid #e0e0e0;
}

.info-row {
    display: flex;
    padding: 0.25rem 0;
    font-size: 12px;
}

.info-row-single {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0;
    font-size: 12px;
    text-align: center;
}

.info-text {
    color: #333;
    white-space: nowrap;
}

.info-separator {
    flex: 1;
    margin: 0 1rem;
}

.info-label {
    width: 80px;
    font-weight: 600;
    color: #666;
}

.info-value {
    flex: 1;
    color: #333;
}

.detail-bottom {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.detail-bottom-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 4px;
}

.detail-btn {
    flex: 1;
    margin: 0px 4px 0px 4px;
}

/* Badge styles */
.badge {
    font-size: 12px;
    font-weight: 500;
    margin-right: 4px;
    display: inline-block;
    min-width: 20px;
    padding: 0px 10px 0px 10px;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 22px;
    border-radius: 8px;
}

.badge.title1 {
    background-color: #ff6d00;
    color: #fff;
}

.badge.title2 {
    background-color: #546e7a;
    color: #fff;
}

.badge.title3 {
    background-color: #00695c;
    color: #fff;
}

.badge.others {
    background-color: #cfd8dc;
    color: #000;
}

.title {
    font-weight: 600;
    margin-right: 20px;
}
</style>
